/* eslint-disable no-console */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Cookies from '../../lib/Cookie';

const getPlaysUsedFromCookie = (cookieName, legacyCookieName, maxPlays) => {
  const stored = Cookies.get(cookieName);
  if (stored !== null && stored !== undefined) {
    const parsed = Number.parseInt(stored, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (Cookies.get(legacyCookieName) === 'true') {
    return maxPlays;
  }

  return 0;
};

const BgAudioPlayer = ({ src, studentId, questionId, audioPlayCount = 1 }) => {
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const progressPercent =
    duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const maxPlays = Math.max(1, Number(audioPlayCount) || 1);
  const cookieName = `audio_plays_used_${studentId}_${questionId}`;
  const legacyCookieName = `audio_played_${studentId}_${questionId}`;

  const [playsUsed, setPlaysUsed] = useState(() =>
    getPlaysUsedFromCookie(cookieName, legacyCookieName, maxPlays),
  );

  const playsRemaining = maxPlays - playsUsed;
  const limitReached = playsUsed >= maxPlays;

  const formatTime = time => {
    if (!time || Number.isNaN(time) || time < 0) return '00:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const playCountLabel = useMemo(() => {
    if (limitReached) {
      return `Plays: ${maxPlays}/${maxPlays} (Limit reached)`;
    }
    return `Plays: ${playsUsed}/${maxPlays} (${playsRemaining} left)`;
  }, [limitReached, maxPlays, playsRemaining, playsUsed]);

  useEffect(() => {
    const used = getPlaysUsedFromCookie(cookieName, legacyCookieName, maxPlays);
    setPlaysUsed(used);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [studentId, questionId, cookieName, legacyCookieName, maxPlays]);

  const handleVolumeChange = event => {
    const newVolume = Number(event.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current && !limitReached) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.log('Play failed:', error);
        });
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || limitReached) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      const nextPlaysUsed = playsUsed + 1;
      setPlaysUsed(nextPlaysUsed);
      Cookies.set(cookieName, String(nextPlaysUsed));

      if (nextPlaysUsed >= maxPlays) {
        setCurrentTime(0);
        audio.pause();
        return;
      }

      audio.currentTime = 0;
      setCurrentTime(0);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    // if (playsUsed === 0) {
    //   audio.play().then(() => {
    //     console.log('Autoplay started');
    //   }).catch(error => {
    //     console.log('Autoplay failed:', error);
    //   });
    // }

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [src, limitReached, cookieName, maxPlays, playsUsed]);

  if (!src) {
    return null;
  }

  return (
    <div className="audio-player">
      <p
        className={
          limitReached ? 'audio-limit-reached' : 'audio-play-count'
        }
      >
        {playCountLabel}
      </p>

      {!limitReached && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
        />
      )}

      <div className="controls">
        <div className="volume-control">
          <div className="volume-control-item">
            <div
              className={`audio-play-btn ${limitReached ? 'is-disabled' : ''}`}
              onClick={togglePlayPause}
              role="presentation"
            >
              {isPlaying ? (
                <span className="fas fa-pause"></span>
              ) : (
                <span className="fas fa-play"></span>
              )}
            </div>

            <div className="audio-track">
              <div className="audio-track-bar">
                <div
                  className="audio-track-progress"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="audio-track-times">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

          </div>
          <div className="volume-control-item-2">
            <span className="fas fa-volume" ></span>
            <input
              type="range"
              id="volume"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              disabled={limitReached}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

BgAudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  studentId: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  audioPlayCount: PropTypes.number,
};

export default BgAudioPlayer;
